import { Suspense, lazy, useEffect, useState } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { Remarkable } from "remarkable";
import TurndownService from "turndown";
import { ToastTypes, withToast } from "../../contexts/toastr.context";
import { CommunityPac } from "../../types/community/community-pac.type";
import { Community } from "../../types/community/community.type";
import { User } from "../../types/user/minimal-user.type";
import { Button, Loader } from "../../_components";
import CommunityLogo from "../../_components/community/community-logo.component";
import IconButton2 from "../../_components/icon-button-2-component";
import PremiumFeature from "../../_components/premium/premium-feature.component";
import { CommunityService } from "../../_service";
import { setCommunity } from "../../_store/_actions/community.actions";
import Validator from "../../_utils/validator";
import { useAppService } from "../../hooks/use-app-service";
const tds = new TurndownService();
const RichTextEditor = lazy(() => import("../../_components/form-controls/rich-text-editor.component"));

export const COMMUNITY_SETTING_WELCOME_MESSAGES_PAGE_ROUTE =
  "/settings/welcome-messages";

interface Props {
  user: User;
  community: Community;
  communityPac: CommunityPac;
  addToast: any;
}
export function CommunitySettingsWelcomeMessagesPageComponent({
  user,
  community,
  communityPac,
  addToast,
}: Props) {
  const { analyticsService } = useAppService();

  const [welcomeMessages, setWelcomeMessages] = useState<string[]>([]);

  useEffect(() => {
    if (!community || Validator.hasValue(welcomeMessages)) return;
    const messageList = community.welcomeMessages;
    let list: Array<string> = [];
    const md = new Remarkable();
    if (Validator.hasValue(community.welcomeMessages)) {
      for (let i = 0; i < messageList?.length; i++) {
        if (community.welcomeMessages[i]) {
          const renderedDescription = md.render(community.welcomeMessages[i]);
          list.push(renderedDescription);
        }
      }
      setWelcomeMessages(list);
    }
  }, [community, welcomeMessages]);

  function getPlainDescription(description: string) {
    if (!description) return "";
    const embeddedDescription = description.replace(
      /(?:<figure class="media"><oembed url=")([^"^<^>^\[^\]]+)(?:"><\/oembed><\/figure>)/g,
      " $1 "
    );

    return tds.turndown(embeddedDescription);
  }

  function addWelcomeMessages() {
    if (welcomeMessages.length > 4) {
      addToast({
        type: ToastTypes.danger,
        message: "You can only add 5 messages",
      });
      return;
    }
    setWelcomeMessages([...welcomeMessages, ""]);
    analyticsService.track("onboard-add-new-message", {
      ...(welcomeMessages && { messagesCount: welcomeMessages.length + 1 })
    })
  }

  function updateWelcomeMessages() {
    let list = [];
    for (let i = 0; i < welcomeMessages?.length; i++) {
      const msg = getPlainDescription(welcomeMessages[i]);
      list.push(msg);
    }
    CommunityService.updateCommunityWelcomeMessages(community.id, list, user)
      .then(({ community }) => {
        setCommunity(community);
        addToast("Welcome messages updated!");
        analyticsService.track("onboarding-message-update", {
          ...(welcomeMessages && { messagesCount: welcomeMessages.length })
        });
      })
      .catch((err) => {
        addToast("Update failed!", "", ToastTypes.danger);
      });
  }

  return (
    <PremiumFeature isAvailable={communityPac.welcomeMessage} hideFeature>
      <div className="UserWallPage flex rounded pensil-card shadow">
        <div className="main-content theme-bg-surface">
          {/* header card */}
          <div className="theme-bg-surface p-4 border-b theme-border-default lex items-start justify-between">
            <div>
              <div className="font-bold theme-text-heading-2">
                Welcome Message
              </div>
              <div className="mt-2 text-sm">
                Greet your members with personalized message
              </div>
            </div>
          </div>

          <div className="p-4">
            {/* welcome card */}
            <div className="flex flex-col items-center my-4 rounded">
              {/*  @ts-ignore */}
              <CommunityLogo community={community} height={148} maxSize />
              <div className="mt-3 mb-1 font-semibold text-xs">Welcome to</div>
              <div className="text-lg font-bold">{community.name}</div>
            </div>
            {/* messages card */}
            {welcomeMessages &&
              welcomeMessages.map((message, index) => (
                <WelcomeMessage
                  message={message}
                  index={index}
                  deleteMessage={() => {
                    const oldWelcomeMessage = [...welcomeMessages];
                    oldWelcomeMessage.splice(index, 1);
                    setWelcomeMessages(oldWelcomeMessage);
                  }}
                  setMessage={(message: string) => {
                    const oldWelcomeMessage = [...welcomeMessages];
                    oldWelcomeMessage[index] = message;
                    setWelcomeMessages(oldWelcomeMessage);
                  }}
                />
              ))}
            {/* add message button */}
            <div className="flex justify-between items-center">
              <Button
                label="+ Add message"
                onClick={() => addWelcomeMessages()}
              />
              <Button label="Update" onClick={updateWelcomeMessages} />
            </div>
          </div>
        </div>
      </div>
    </PremiumFeature>
  );
}

interface WelcomeMessageProps {
  message: string;
  index: number;
  setMessage: (message: string) => void;
  deleteMessage: () => void;
}
function WelcomeMessage({
  message,
  index,
  setMessage,
  deleteMessage,
}: WelcomeMessageProps) {
  return (
    <div className="mb-2">
      <div className="font-bold my-2 theme-text-subtitle-1 uppercase text-xs flex items-center gap-1">
        <div>Message #{index + 1}</div>
        <IconButton2
          icon="trash"
          size="sm"
          iconClass="theme-text-danger "
          className="h-5 w-5"
          onClick={deleteMessage}
        />
      </div>
      <div className="border theme-border-default rounded">
        <Suspense fallback={<></>}>
          <RichTextEditor
            text={message}
            setText={setMessage}
            onSubmit={undefined}
            className="noBorder minHeightDefault px-2 theme-bg-default"
          />
        </Suspense>
      </div>
    </div>
  );
}

const CommunitySettingsWelcomeMessagesPage = withToast(
  connect(
    (s: any) => ({
      community: s.community,
      communityPac: s.communityPac,
      user: s.auth,
    }),
    (d: any) =>
      bindActionCreators(
        {
          updateCommunity: setCommunity,
        },
        d
      )
  )(CommunitySettingsWelcomeMessagesPageComponent)
);

export default CommunitySettingsWelcomeMessagesPage;
